<template>
  <div class="w-full">
    <button
      v-if="actionButton?.enabled"
      :class="[
        'absolute',
        'top-[-40px]',
        'h-40',
        'rounded-tl-md',
        'rounded-tr-md',
        'bg-surface-highlight',
        'px-16',
        'py-8',
        'gap-8',
        'flex',
        'items-center',
        'heading-xxs',
        'border-sm',
        'border-border-tertiary',
        'hover:bg-surface-action-hover',
        'hover:text-text-on-action',
        'focus:ring-8',
        'focus:ring-white-500',
        'text-text-on-highlight',
      ]"
      :aria-expanded="actionButton.ariaExpanded"
      @click="emit('button-click')"
    >
      {{ actionButton.text }}
      <nuxt-icon :name="actionButton.iconName" aria-hidden="true" />
    </button>

    <div class="flex justify-between">
      <div
        v-if="desktopUsps.length > 0"
        class="hidden lg:flex flex-row items-center me-24"
      >
        <AppUsp :items="desktopUsps" usp-direction="row" />
      </div>

      <div class="flex flex-col md:flex-row">
        <div
          class="flex flex-col md:me-16 md:flex-row md:gap-8 md:items-center"
        >
          <!-- Strikethrough non-discount price -->
          <p
            v-if="strikeThroughPriceExistsAndDiffers && isBindingPeriodActive"
            class="strike-through text-text-secondary heading-xxs w-fit"
            aria-hidden="true"
          >
            {{ currentTotalRegularMonthlyPriceToDisplay }}
          </p>

          <!-- Main price to the left-->
          <div class="flex flex-row gap-8 items-center" aria-hidden="true">
            <p
              :class="{
                'text-text-success': strikeThroughPriceExistsAndDiffers,
              }"
              class="heading-xl"
            >
              {{
                showOnlyDirectPrice
                  ? currentTotalDirectPriceToDisplay
                  : currentTotalMonthlyPriceToDisplay
              }}
            </p>
            <div
              :class="{
                'text-text-success': strikeThroughPriceExistsAndDiffers,
              }"
              class="flex flex-col"
            >
              <p class="heading-xxs">
                {{ showOnlyDirectPrice ? 'kr' : 'kr/mån' }}
              </p>
              <p v-if="isMonthlyPayment" class="heading-xxs">
                {{ inXMonthsText }}
              </p>
            </div>
          </div>
        </div>

        <!-- Direct price to the right-->
        <div
          v-if="!showOnlyDirectPrice"
          class="flex items-center gap-5 md:gap-8"
          aria-hidden="true"
        >
          <p
            :class="[
              'md:heading-xl',
              'md:text-text-primary',
              'heading-xxs',
              'text-text-success',
            ]"
          >
            {{ currentTotalDirectPriceToDisplay }}
          </p>
          <div class="flex flex-row gap-5 md:gap-0 md:flex-col">
            <p class="heading-xxs text-text-success md:text-text-primary">
              kr att
            </p>
            <p class="heading-xxs text-text-success md:text-text-primary">
              betala nu
            </p>
          </div>
        </div>
      </div>

      <div aria-live="polite">
        <p class="sr-only">
          {{ currentFullPriceStringForScreenReader }}
        </p>
      </div>
    </div>
    <p v-if="extraTextLine" class="heading-xxs ms-24">{{ extraTextLine }}</p>
  </div>
</template>

<script lang="ts" setup>
import type { UspItem } from '~/models/UspItem.model';

interface Props {
  strikeThroughPriceExistsAndDiffers: boolean;
  currentTotalRegularMonthlyPriceToDisplay: string;
  currentTotalMonthlyPriceToDisplay: string;
  inXMonthsText: string;
  currentTotalDirectPriceToDisplay: string;
  currentFullPriceStringForScreenReader: string;
  isBindingPeriodActive: boolean;
  isMonthlyPayment: boolean;
  showOnlyDirectPrice?: boolean;
  extraTextLine?: string;
  actionButton?: {
    enabled: boolean;
    iconName: string;
    text: string;
    ariaExpanded: boolean;
  };
  desktopUsps?: UspItem[];
}

withDefaults(defineProps<Props>(), {
  extraTextLine: '',
  actionButton: undefined,
  desktopUsps: () => [],
});

const emit = defineEmits<{
  'button-click': [];
}>();
</script>
